import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { ChevronDown, ChevronRight, Clock, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { SecondaryPanelContent } from '@/components/layout/secondary-panel-content'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { DiffPreviewDialog } from '@/components/layout/diff-preview-dialog'
import { fetchWithAuth, resolveDocumentRowId, updateDocument } from '@/lib/api'
import { detectShortcutOs, modKeyLabel } from '@/lib/keyboard-platform'
import { parseReviewChangelogFence } from '@/lib/knowledge-hub-utils'
import { isModClick } from '@/lib/open-intent'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { useWorkspace } from '@/providers/workspace-provider'
import {
  buildVersionTimeline,
  buildVersionTimelineWithChangelog,
  clusterLatestNote,
  clusterSummaryBadges,
  clusterTimelineItemsByMinute,
  cumulativeDiffForSelection,
  editHasDiffContent,
  formatChangeSummary,
  formatClusterTimeLabel,
  formatCumulativeRevisionTitle,
  formatEditAttribution,
  formatEditSourceLabel,
  formatRevisionTimestamp,
  formatSingleRevisionTitle,
  mergeDocumentVersionsIntoEdits,
  milestoneBadgeColor,
  type DocumentEditRow,
  type DocumentVersionRow,
  type TimelineTimeCluster,
  type TimelineVersionParent,
} from '@/lib/document-version-history'

/** Compact OS-aware compare hint: ⌘/Ctrl chip + short copy. */
function ModCompareHint({ className }: { className?: string }) {
  const mod = modKeyLabel(detectShortcutOs())
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs text-muted-foreground select-none',
        className
      )}
    >
      <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-sm border border-border/50 bg-muted/50 px-1 font-sans text-[11px] leading-none text-foreground/80">
        {mod}
      </kbd>
      <span aria-hidden className="text-muted-foreground/70">
        +
      </span>
      <span>click to compare</span>
    </span>
  )
}

type Props = {
  documentId: string
  onClose: () => void
  /** Document's display title/filename, shown in the revision diff dialog. Falls back to a generic label when omitted. */
  documentTitle?: string
  /**
   * Storage / pretty path when `documentId` may still be a path-keyed tab id (cold deep link).
   * Used to resolve `documents.id` before calling `/:id/history`.
   */
  documentPath?: string | null
  /**
   * Current document body, used to detect and merge a Knowledge Hub `review-changelog`
   * fence into this timeline (see document-version-history.ts buildVersionTimelineWithChangelog).
   * Omit for surfaces that don't track document body (falls back to document_edits only).
   */
  documentBody?: string
  /** Shows the "Append changelog note" affordance (ported from the standalone ReviewChangelogPanel) when true. */
  reviewEnabled?: boolean
  /**
   * Called after a changelog note is successfully appended (or a restore), so the caller can
   * refresh its cached document body. When `nextBody` is provided, use it directly (avoids a
   * stale tab-id refetch race).
   */
  onDocumentBodyChange?: (nextBody?: string) => void
  /**
   * When true, omit SecondaryPanelContent chrome (Document Info dock owns header + close).
   * Loading/error still render inline in the body.
   */
  embedded?: boolean
}

/** Map selection ids (parent or child) to the DocumentEditRow used for diffs. */
function buildSelectableMap(
  timeline: ReturnType<typeof buildVersionTimeline>
): Map<string, DocumentEditRow> {
  const map = new Map<string, DocumentEditRow>()
  for (const day of timeline) {
    for (const parent of day.items) {
      if (parent.edit) {
        map.set(parent.id, parent.edit)
        map.set(parent.edit.id, parent.edit)
      }
      for (const child of parent.children) {
        map.set(child.id, child)
      }
    }
  }
  return map
}

/** Secondary panel: GDocs-aligned version history (click-select, nested edits, Cmd/Ctrl+click compare). */
export function DocumentVersionHistoryPanel({
  documentId,
  onClose,
  documentTitle,
  documentPath,
  documentBody,
  reviewEnabled,
  onDocumentBodyChange,
  embedded = false,
}: Props) {
  const [edits, setEdits] = useState<DocumentEditRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set())
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set())
  /** Multi-item same-minute clusters start collapsed; ids present here are expanded. */
  const [expandedTimeClusters, setExpandedTimeClusters] = useState<Set<string>>(new Set())
  const [showDiffDialog, setShowDiffDialog] = useState(false)
  const [diffContent, setDiffContent] = useState<{ before: string; after: string; title: string } | null>(null)
  const [appendNote, setAppendNote] = useState('')
  const [appending, setAppending] = useState(false)
  const [appendError, setAppendError] = useState<string | null>(null)
  const [restoring, setRestoring] = useState(false)
  const [resolvedDocumentId, setResolvedDocumentId] = useState<string | null>(null)
  const { workspaces } = useWorkspace()

  const fenceEntries = useMemo(
    () => (documentBody ? parseReviewChangelogFence(documentBody) : []),
    [documentBody]
  )
  const timeline = useMemo(
    () =>
      fenceEntries.length > 0
        ? buildVersionTimelineWithChangelog(edits, fenceEntries)
        : buildVersionTimeline(edits),
    [edits, fenceEntries]
  )

  const selectableById = useMemo(() => buildSelectableMap(timeline), [timeline])

  const selectedEdits = useMemo(
    () =>
      selectedIds
        .map((id) => selectableById.get(id))
        .filter((e): e is DocumentEditRow => Boolean(e)),
    [selectedIds, selectableById]
  )

  // Default: expand today's day + parents that have children (first load / timeline change).
  useEffect(() => {
    if (timeline.length === 0) return
    const todayKey = new Date().toDateString()
    setExpandedDays((prev) => {
      if (prev.size > 0) return prev
      const next = new Set<string>()
      for (const day of timeline) {
        if (day.dateKey === todayKey || timeline.length === 1) next.add(day.dateKey)
      }
      if (next.size === 0 && timeline[0]) next.add(timeline[0].dateKey)
      return next
    })
    setExpandedParents((prev) => {
      if (prev.size > 0) return prev
      const next = new Set<string>()
      for (const day of timeline) {
        if (day.dateKey !== todayKey && timeline.length > 1) continue
        for (const parent of day.items) {
          if (parent.children.length > 0) next.add(parent.id)
        }
      }
      return next
    })
  }, [timeline])

  const loadHistory = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const pathFallback =
        documentPath?.trim() || (documentId.includes('/') ? documentId : null)
      const uuid = await resolveDocumentRowId(documentId, pathFallback, { workspaces })
      setResolvedDocumentId(uuid)
      const res = await fetchWithAuth(
        `/api/documents/${uuid}/history?limit=200&includeContent=true`
      )
      if (!res.ok) {
        throw new Error('Failed to load version history')
      }
      const data = await res.json()
      const history = (data.history ?? []) as DocumentEditRow[]
      const versions = (data.versions ?? []) as DocumentVersionRow[]
      setEdits(mergeDocumentVersionsIntoEdits(history, versions))
      setSelectedIds([])
      setExpandedDays(new Set())
      setExpandedParents(new Set())
      setExpandedTimeClusters(new Set())
    } catch (err) {
      setResolvedDocumentId(null)
      setError(err instanceof Error ? err.message : 'Failed to load version history')
      setEdits([])
    } finally {
      setLoading(false)
    }
  }, [documentId, documentPath, workspaces])

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  const openSingleEditDiff = (edit: DocumentEditRow) => {
    if (!editHasDiffContent(edit)) return
    setDiffContent({
      before: edit.content_before!,
      after: edit.content_after!,
      title: formatSingleRevisionTitle(edit),
    })
    setShowDiffDialog(true)
  }

  const openCumulativeDiff = () => {
    const cumulative = cumulativeDiffForSelection(selectedEdits)
    if (!cumulative) return
    setDiffContent({ ...cumulative, title: formatCumulativeRevisionTitle(selectedEdits) })
    setShowDiffDialog(true)
  }

  const handleAppendNote = async () => {
    const note = appendNote.trim()
    if (!note) {
      setAppendError('Enter a note to append')
      return
    }
    const docId = resolvedDocumentId
    if (!docId) {
      setAppendError('Document id not ready - wait for history to load, then retry')
      toast.error('Document id not ready')
      return
    }
    setAppending(true)
    setAppendError(null)
    try {
      const res = await fetchWithAuth('/api/knowledge-hub/append-changelog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_id: docId,
          action: 'review-requested',
          note,
        }),
      })
      if (!res.ok) {
        const payload = await res.json().catch(() => ({} as { error?: string; details?: string }))
        const message =
          payload.error ||
          payload.details ||
          (res.status === 403
            ? 'Knowledge Hub is not enabled for this workspace'
            : res.status === 404
              ? 'Document not found'
              : 'Failed to append changelog note')
        setAppendError(message)
        toast.error(message)
        return
      }
      const data = (await res.json().catch(() => null)) as
        | { document?: { content?: string | null } }
        | null
      const nextBody =
        typeof data?.document?.content === 'string' ? data.document.content : undefined
      setAppendNote('')
      setAppendError(null)
      onDocumentBodyChange?.(nextBody)
      window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId: docId } }))
      toast.success('Changelog note appended')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to append changelog note'
      setAppendError(message)
      toast.error(message)
    } finally {
      setAppending(false)
    }
  }

  const handleRestore = async () => {
    if (!diffContent) return
    const docId = resolvedDocumentId ?? documentId
    setRestoring(true)
    try {
      await updateDocument(
        docId,
        {
          content: diffContent.after,
          saveEditHistory: true,
          changedBy: 'user',
          writeToFile: true,
          historyMetadata: { saveTrigger: 'restore' },
        },
        { pathFallback: documentPath }
      )
      setShowDiffDialog(false)
      onDocumentBodyChange?.(diffContent.after)
      window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId: docId } }))
      await loadHistory()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to restore this version')
    } finally {
      setRestoring(false)
    }
  }

  const activateRow = (id: string, e: MouseEvent, edit: DocumentEditRow | undefined) => {
    if (isModClick(e)) {
      e.preventDefault()
      setSelectedIds((prev) => {
        if (prev.includes(id)) return prev.filter((x) => x !== id)
        if (prev.length === 0) return [id]
        if (prev.length === 1) return prev[0] === id ? prev : [prev[0], id]
        return [prev[0], id]
      })
      return
    }
    setSelectedIds([id])
    if (edit && editHasDiffContent(edit)) {
      openSingleEditDiff(edit)
    }
  }

  /** Block native text selection when Cmd/Ctrl+clicking to compare (Shift+click used to paint a selection). */
  const preventCompareTextSelect = (e: MouseEvent) => {
    if (isModClick(e)) e.preventDefault()
  }

  const toggleParent = (id: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleDay = (dateKey: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev)
      if (next.has(dateKey)) next.delete(dateKey)
      else next.add(dateKey)
      return next
    })
  }

  const toggleTimeCluster = (id: string) => {
    setExpandedTimeClusters((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const renderParentRow = (
    parent: TimelineVersionParent,
    isTip: boolean,
    options?: { hideTime?: boolean }
  ) => {
    const hideTime = options?.hideTime ?? false
    const selected = selectedIds.length === 1 && selectedIds[0] === parent.id
    const compareSelected = selectedIds.length === 2 && selectedIds.includes(parent.id)
    const expanded = expandedParents.has(parent.id)
    const hasChildren = parent.children.length > 0
    const canDiff = parent.edit ? editHasDiffContent(parent.edit) : false
    const badgeColor = milestoneBadgeColor(parent)
    const attribution = parent.edit
      ? formatEditAttribution(parent.edit)
      : parent.changedBy
    const isNamed = parent.variant === 'named'

    return (
      <div key={parent.id} className="space-y-0.5">
        <div
          role="button"
          tabIndex={0}
          data-testid={testIds.documentVersionHistory.milestoneRow(parent.id)}
          onMouseDown={preventCompareTextSelect}
          onClick={(e) => activateRow(parent.id, e, parent.edit)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              activateRow(parent.id, e as unknown as MouseEvent, parent.edit)
            }
          }}
          className={cn(
            'group flex items-start gap-1 rounded-sm px-1.5 py-2 text-sm w-full text-left transition-colors select-none',
            'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            selected && 'bg-muted/50',
            compareSelected && !selected && 'bg-primary/5 ring-1 ring-primary/25',
            !canDiff && !hasChildren && 'opacity-70'
          )}
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            {hasChildren ? (
              <button
                type="button"
                className="p-0.5 rounded hover:bg-muted text-muted-foreground"
                aria-label={expanded ? 'Collapse edits' : 'Expand edits'}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleParent(parent.id)
                }}
              >
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="w-3.5" aria-hidden />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              {!hideTime ? (
                <span
                  className="text-muted-foreground tabular-nums shrink-0"
                  title={formatRevisionTimestamp(parent.timestamp)}
                >
                  {formatClusterTimeLabel(parent.timestamp)}
                </span>
              ) : null}
              {attribution ? (
                <span className="text-foreground/80 truncate">{attribution}</span>
              ) : null}
              <Badge
                variant={isNamed ? 'secondary' : 'outline'}
                className={cn(
                  'h-4 px-1.5 text-[10px] font-normal border whitespace-nowrap shrink-0',
                  isNamed && 'font-semibold',
                  badgeColor
                )}
              >
                {parent.label}
              </Badge>
              {isTip ? (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  Current version
                </span>
              ) : null}
            </div>
            {parent.reason ? (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{parent.reason}</p>
            ) : null}
            {!canDiff && parent.variant === 'named' && !parent.edit ? (
              <p className="text-xs text-muted-foreground mt-0.5">Diff unavailable</p>
            ) : null}
          </div>
        </div>

        {expanded
          ? parent.children.map((child) => {
              const childSelected = selectedIds.length === 1 && selectedIds[0] === child.id
              const childCompare = selectedIds.length === 2 && selectedIds.includes(child.id)
              const childCanDiff = editHasDiffContent(child)
              const sourceLabel = formatEditSourceLabel(child)
              return (
                <div key={child.id} className="pl-5">
                  <div
                    role="button"
                    tabIndex={0}
                    data-testid={testIds.documentVersionHistory.row(child.id)}
                    onMouseDown={preventCompareTextSelect}
                    onClick={(e) => activateRow(child.id, e, child)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        activateRow(child.id, e as unknown as MouseEvent, child)
                      }
                    }}
                    className={cn(
                      'flex items-start gap-1 rounded-sm px-1.5 py-2 text-sm w-full text-left transition-colors select-none',
                      'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                      childSelected && 'bg-muted/50',
                      childCompare && !childSelected && 'bg-primary/5 ring-1 ring-primary/25',
                      !childCanDiff && 'opacity-60'
                    )}
                  >
                    <div className="w-5 shrink-0" aria-hidden />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        {!hideTime ? (
                          <span
                            className="text-muted-foreground tabular-nums shrink-0"
                            title={formatRevisionTimestamp(child.timestamp)}
                          >
                            {formatClusterTimeLabel(child.timestamp)}
                          </span>
                        ) : null}
                        <span className="text-foreground/80 truncate">
                          {formatEditAttribution(child)}
                        </span>
                        {sourceLabel ? (
                          <Badge
                            variant="outline"
                            className="h-4 px-1.5 text-[10px] font-normal text-muted-foreground border-border whitespace-nowrap shrink-0"
                          >
                            {sourceLabel}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                        {child.metadata?.reason ? (
                          <div className="line-clamp-2">{child.metadata.reason}</div>
                        ) : null}
                        <div>
                          {formatChangeSummary(child)}
                          {!childCanDiff ? ' (diff unavailable)' : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          : null}
      </div>
    )
  }

  const tipParentId = timeline[0]?.items[0]?.id

  const renderTimeCluster = (cluster: TimelineTimeCluster) => {
    const multi = cluster.items.length > 1
    const clusterOpen = !multi || expandedTimeClusters.has(cluster.id)
    const summaryBadges = multi ? clusterSummaryBadges(cluster.items) : []
    const latestNote = multi ? clusterLatestNote(cluster.items) : undefined
    const containsTip = cluster.items.some((item) => item.id === tipParentId)

    return (
      <div
        key={cluster.id}
        className="rounded-md bg-muted/15 px-0.5 py-1"
        data-testid={testIds.documentVersionHistory.timeCluster(cluster.id)}
      >
        {multi ? (
          <button
            type="button"
            onClick={() => toggleTimeCluster(cluster.id)}
            className={cn(
              'flex w-full items-start gap-1.5 rounded-sm px-1.5 py-2 text-left transition-colors select-none',
              'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
            )}
            aria-expanded={clusterOpen}
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
              {clusterOpen ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </span>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <span
                  className="text-sm font-medium tabular-nums text-foreground/90 shrink-0"
                  title={formatRevisionTimestamp(cluster.timestamp)}
                >
                  {cluster.timeLabel}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {cluster.items.length} events
                </span>
                {containsTip ? (
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Current
                  </span>
                ) : null}
              </div>
              {!clusterOpen ? (
                <>
                  {summaryBadges.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {summaryBadges.map((label) => (
                        <Badge
                          key={`${cluster.id}-${label}`}
                          variant="outline"
                          className="h-4 px-1.5 text-[10px] font-normal border-border/60 text-muted-foreground"
                        >
                          {label}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                  {latestNote ? (
                    <p className="text-xs text-muted-foreground line-clamp-2">{latestNote}</p>
                  ) : null}
                </>
              ) : null}
            </div>
          </button>
        ) : (
          <div className="flex items-center gap-1.5 px-1.5 pt-1 pb-0.5">
            <span className="w-5 shrink-0" aria-hidden />
            <span
              className="text-sm font-medium tabular-nums text-foreground/90"
              title={formatRevisionTimestamp(cluster.timestamp)}
            >
              {cluster.timeLabel}
            </span>
          </div>
        )}

        {clusterOpen
          ? (
            <div className={cn('space-y-1', multi ? 'pl-1' : undefined)}>
              {cluster.items.map((parent) =>
                renderParentRow(parent, parent.id === tipParentId, { hideTime: true })
              )}
            </div>
          ) : null}
      </div>
    )
  }

  const body = (
    <>
      <div className="flex min-h-full flex-col" data-testid={testIds.documentVersionHistory.panel}>
        {loading ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : edits.length === 0 && fenceEntries.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6">
            <Clock className="h-12 w-12 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No edit history yet</p>
          </div>
        ) : (
          <div className="flex-1 space-y-4 p-2 select-none">
            {timeline.map((row) => {
              const dayOpen = expandedDays.has(row.dateKey)
              const clusters = clusterTimelineItemsByMinute(row.items)
              return (
                <div key={`day-${row.dateKey}`} className="space-y-2">
                  <button
                    type="button"
                    onClick={() => toggleDay(row.dateKey)}
                    className="flex w-full items-center gap-1 px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
                  >
                    {dayOpen ? (
                      <ChevronDown className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                    {row.dateLabel}
                  </button>
                  {dayOpen ? (
                    <div className="space-y-2.5">{clusters.map(renderTimeCluster)}</div>
                  ) : null}
                </div>
              )
            })}
          </div>
        )}

        <div className="sticky bottom-0 shrink-0 bg-card/95 backdrop-blur-sm supports-[backdrop-filter]:bg-card/80">
          <div className="mx-3 border-t border-border/40" aria-hidden />
          <div className="flex items-center gap-2 px-3 py-2">
            {selectedIds.length >= 2 ? (
              <Button
                variant="outline"
                size="sm"
                className="h-7"
                onClick={openCumulativeDiff}
                disabled={!cumulativeDiffForSelection(selectedEdits)}
              >
                Compare selected ({selectedEdits.length})
              </Button>
            ) : (
              <ModCompareHint />
            )}
          </div>

          {reviewEnabled ? (
            <div
              className="space-y-2 px-3 pb-3"
              data-testid={testIds.knowledgeHub.changelogPanel}
            >
              <div className="flex items-center gap-2">
                <Input
                  value={appendNote}
                  onChange={(e) => {
                    setAppendNote(e.target.value)
                    if (appendError) setAppendError(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      void handleAppendNote()
                    }
                  }}
                  placeholder="Add a review note..."
                  disabled={appending || !resolvedDocumentId}
                  className="h-8 flex-1 bg-muted/30 border-border/50"
                  data-testid={testIds.documentVersionHistory.appendNoteInput}
                  aria-invalid={Boolean(appendError)}
                />
                <Button
                  size="sm"
                  className="h-8 shrink-0"
                  onClick={() => void handleAppendNote()}
                  disabled={appending || !appendNote.trim() || !resolvedDocumentId}
                  data-testid={testIds.documentVersionHistory.appendNoteButton}
                >
                  {appending ? (
                    <>
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      Append
                    </>
                  ) : (
                    'Append'
                  )}
                </Button>
              </div>
              {appendError ? (
                <p className="text-xs text-destructive" role="alert">
                  {appendError}
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Appends to the Knowledge Hub changelog
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {diffContent ? (
        <div data-testid={testIds.documentVersionHistory.diffDialog}>
          <DiffPreviewDialog
            open={showDiffDialog}
            onOpenChange={setShowDiffDialog}
            original={diffContent.before}
            proposed={diffContent.after}
            filename={documentTitle || 'Document'}
            title={diffContent.title}
            readOnly
            onRestore={handleRestore}
            restoring={restoring}
          />
        </div>
      ) : null}
    </>
  )

  if (embedded) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-card">
        <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
      </div>
    )
  }

  return (
    <SecondaryPanelContent
      title="Version history"
      onClose={onClose}
      loading={false}
      error={null}
      headerActions={
        selectedIds.length === 1 ? (
          <ModCompareHint className="px-1 whitespace-nowrap" />
        ) : selectedIds.length >= 2 ? (
          <Button
            variant="outline"
            size="sm"
            onClick={openCumulativeDiff}
            disabled={!cumulativeDiffForSelection(selectedEdits)}
          >
            Compare selected ({selectedEdits.length})
          </Button>
        ) : null
      }
    >
      {body}
    </SecondaryPanelContent>
  )
}
